import React, { useState, useMemo } from 'react';
import { Accordion, Column, Grid, Icon } from 'components';
import { CLPFilter, Category } from '../../../../types/Category';
import { getFilterTitle, getSingleFilterState } from '../../../CLPFilters/helpers';
import { productMatchesFilters } from 'components/CLP/useFilters';
import { caseInsensitiveSlugMatch } from 'helpers/strings';
import {
  FilterButton,
  FilterCheckbox,
  FilterLabel,
  FilterOption,
  FilterOptionWrapper,
  FiltersModalBody,
  FiltersModalFooter
} from './styled';
import { useFilterHandlers } from '../../../../hooks/useFilterHandlers';
import { useSelector } from 'react-redux';
import { filterStateSelector } from 'store/ui/selectors';

interface FiltersModalProps {
  filters: CLPFilter[];
  category: Category;
  onClose: () => void;
  onClear: () => void;
}

const Filters = (props: FiltersModalProps): React.ReactElement => {
  // Data
  const {
    filters,
    category,
    onClose,
    onClear
  } = props;
  const filterState = useSelector(filterStateSelector);
  const { handleFilterChange, handleFilterClear } = useFilterHandlers(filters);

  const [openAccordions, setOpenAccordions] = useState(filters.length > 0 ? [filters[0].label] : []);

  const productCountWithFilters = useMemo(() => {
    const filteredProducts = category.products.filter(p => productMatchesFilters(p, filterState.filters));
    return filteredProducts.length;
  }, [category, filterState]);

  const handleClear = () => {
    handleFilterClear();
    onClear();
  };

  const getButtonText = () => {
    if (productCountWithFilters === 0) {
      return 'No Results';
    }
    return `View Products (${productCountWithFilters})`;
  };

  const isFilterChecked = (filter: CLPFilter, option: string): boolean => {
    const thisFilterState: CLPFilter = getSingleFilterState(filterState, filter.attributeCode);
    return thisFilterState.options.some((_option: string) => caseInsensitiveSlugMatch(_option, option));
  };

  return (
    <>
      <FiltersModalBody>
        <div style={{ paddingBottom: '100px' }}>
          {filters.map((filter: CLPFilter) => (
            <Accordion
              key={filter.label}
              id={filter.label}
              isHtmlString={false}
              title={getFilterTitle(filter.label, filter.attributeCode)}
              content={
                <FilterAccordionContent
                  filter={filter}
                  onChange={handleFilterChange}
                  isFilterChecked={isFilterChecked}
                />
              }
              active={openAccordions.includes(filter.label) ? filter.label : ''}
              open={openAccordions.includes(filter.label)}
              setActive={(id) => {
                const newAccordions = id === ''
                  ? openAccordions.filter(accordion => accordion !== filter.label)
                  : [
                    ...openAccordions,
                    filter.label
                  ];
                setOpenAccordions(newAccordions);
              }} />
          ))}
        </div>
      </FiltersModalBody>
      <FiltersModalFooter>
        <Grid style={{ width: '100%' }}>
          <Column size={6}>
            <FilterButton onClick={handleClear} disabled={!productCountWithFilters}>
              Clear Filters
            </FilterButton>
          </Column>
          <Column size={6}>
            <FilterButton onClick={onClose} disabled={!productCountWithFilters}>
              {getButtonText()}
            </FilterButton>
          </Column>
        </Grid>
      </FiltersModalFooter>
    </>
  );
};

interface FilterAccordionContentProps {
  filter: CLPFilter;
  onChange: (attributeCode: string, option: string) => void;
  isFilterChecked: (filter: CLPFilter, option: string) => boolean;
}

const FilterAccordionContent: React.FC<FilterAccordionContentProps> = ({
  filter,
  onChange,
  isFilterChecked
}) => (
  <Grid>
    {filter.options.map((option: string) => (
      <Column key={option} size={6}>
        <FilterOptionWrapper key={option}>
          <FilterLabel htmlFor={option}>
            <FilterCheckbox
              id={option}
              name={option}
              type="checkbox"
              onChange={(event): void => onChange(filter.attributeCode, event.target.name)}
              checked={isFilterChecked(filter, option)} />
            <Icon icon="check" color="#FFFFFF" />
            <FilterOption>{option}</FilterOption>
          </FilterLabel>
        </FilterOptionWrapper>
      </Column>
    ))}
  </Grid>
);

export default Filters;
